/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

import tutorsParollTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData";

// import DraftTutorePayrollTableData from "Views/FinanceManagment/DraftTutorSalary/data/draftTutorSalaryTableData";

// import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";

import AllTransactionTableData from "Views/FinanceManagment/AllTransaction/data/AllTransactionTableData";

import { useState, useEffect } from "react";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import PaginationBar from "components/PaginationBar/PaginationBar";
import FinanceReport from "./FinanceReport";

function Tables() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [mode, setMode] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const getTransactionUser = (transaction) => {
    const isRegistrationFees = transaction?.FeesType === "REGISTRATION_FEES";
    return isRegistrationFees
      ? transaction?.tutorId || transaction?.parentId
      : transaction?.parentId || transaction?.tutorId;
  };

  const getPaymentDate = (transaction) =>
    transaction?.paymentDate ||
    transaction?.tutorPaymentCycleId?.paymentDate ||
    transaction?.paymentDateFallback ||
    transaction?.financeId?.classSessionId?.paymentDate ||
    transaction?.financeId?.classSessionId?.scheduleDate ||
    transaction?.financeId?.classSessionId?.firstClassOn;

  const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });

  const formatLabel = (value) => String(value || "N/A").replace(/_/g, " ");

  const handleViewTransaction = async (transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleTransactionSeen = (_response, transaction) => {
    setSelectedTransaction((currentTransaction) =>
      currentTransaction?._id === transaction?._id
        ? {
            ...currentTransaction,
            adminUnread: false,
            adminSeenAt: new Date().toISOString(),
          }
        : currentTransaction
    );
    fetchTransactions();
  };

  const selectedUser = getTransactionUser(selectedTransaction);

  const detailRows = selectedTransaction
    ? [
        ["Transaction ID", selectedTransaction.transactionId || selectedTransaction._id || "N/A"],
        ["Transaction Type", formatLabel(selectedTransaction.transactionType)],
        ["Method", formatLabel(selectedTransaction.method)],
        ["Fees Type", formatLabel(selectedTransaction.FeesType)],
        ["Payment Date", getPaymentDate(selectedTransaction) ? dayjs(getPaymentDate(selectedTransaction)).format("DD MMMM YYYY") : "N/A"],
        ["Created At", selectedTransaction.createdAt ? dayjs(selectedTransaction.createdAt).format("DD MMM YYYY, hh:mm A") : "N/A"],
        ["Updated At", selectedTransaction.updatedAt ? dayjs(selectedTransaction.updatedAt).format("DD MMM YYYY, hh:mm A") : "N/A"],
        ["Notes", selectedTransaction.notes || selectedTransaction.remark || "N/A"],
      ]
    : [];

  const { columns, rows, loading, fetchTransactions, pagination } = AllTransactionTableData(
    filters,
    handleViewTransaction,
    handleTransactionSeen
  );

  // Update sno for rows with correct page offset
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
  const rowsWithCorrectSno = rows.map((row, index) => ({
    ...row,
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {startIndex + index + 1}
      </ArgonTypography>
    ),
  }));

  useEffect(() => {
    setFilters({
      fromDate,
      toDate,
      method: mode,
      transactionType,
      page: currentPage,
      limit: rowsPerPage,
    });
  }, [fromDate, toDate, mode, transactionType, currentPage, rowsPerPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleResetFilters = () => {
    setFromDate(null);
    setToDate(null);
    setMode("");
    setTransactionType("");
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, mode, transactionType]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  All Transaction
                </ArgonTypography>
                <FinanceReport />
              </ArgonBox>

              <ArgonBox
                display="flex"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                    minDate={fromDate}
                  />
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleResetFilters}
                    disabled={!fromDate && !toDate && !mode && !transactionType}
                  >
                    Reset
                  </ArgonButton>
                </ArgonBox>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <Dropdown
                    label="Transaction Type"
                    value={transactionType}
                    options={[
                      { label: "ALL", value: "" },
                      { label: "CREDIT", value: "CREDIT" },
                      { label: "DEBIT", value: "DEBIT" },
                    ]}
                    onChange={(e) => setTransactionType(e.target.value)}
                    width={150}
                  />
                  <Dropdown
                    label="Mode"
                    value={mode}
                    options={[
                      { label: "ALL", value: "" },
                      { label: "CASH", value: "CASH" },
                      { label: "ONLINE", value: "ONLINE" },
                    ]}
                    onChange={(e) => setMode(e.target.value)}
                    width={150}
                  />
                  
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                // minHeight: "400px", // Fixed height to prevent layout shift
              }}
            >
              {loading ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Transaction found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rowsWithCorrectSno} />
              )}

              {/* Pagination */}
              {pagination.totalItems > 0 && (
                <PaginationBar
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  totalItems={pagination.totalItems}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Dialog
        open={Boolean(selectedTransaction)}
        onClose={() => setSelectedTransaction(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: "linear-gradient(135deg, #263c87 0%, #11cdef 100%)",
          }}
        >
          <ArgonBox p={2.5} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
                Transaction Details
              </ArgonTypography>
              <ArgonTypography variant="caption" sx={{ color: "rgba(255,255,255,0.82)" }}>
                {selectedTransaction?.transactionId || selectedTransaction?._id || "Transaction"}
              </ArgonTypography>
            </ArgonBox>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              {selectedTransaction?.transactionType && (
                <Chip
                  label={selectedTransaction.transactionType}
                  size="small"
                  sx={{
                    color: "#0f172a",
                    backgroundColor: "#fff",
                    fontWeight: 800,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => setSelectedTransaction(null)}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.16)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.26)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ArgonBox>
          </ArgonBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedTransaction && (
            <ArgonBox p={2.5} display="flex" flexDirection="column" gap={2}>
              <ArgonBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{
                  p: 1.75,
                  borderRadius: "12px",
                  backgroundColor:
                    selectedTransaction.transactionType === "DEBIT"
                      ? "rgba(245, 54, 92, 0.08)"
                      : "rgba(45, 206, 137, 0.08)",
                  border:
                    selectedTransaction.transactionType === "DEBIT"
                      ? "1px solid rgba(245, 54, 92, 0.22)"
                      : "1px solid rgba(45, 206, 137, 0.22)",
                }}
              >
                <ArgonBox>
                  <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                    Amount
                  </ArgonTypography>
                  <ArgonTypography
                    variant="h4"
                    fontWeight="bold"
                    color={selectedTransaction.transactionType === "DEBIT" ? "error" : "success"}
                  >
                    {formatAmount(selectedTransaction.amount)}
                  </ArgonTypography>
                </ArgonBox>
                <Chip
                  label={formatLabel(selectedTransaction.FeesType)}
                  size="small"
                  color="default"
                  sx={{ fontWeight: 700 }}
                />
              </ArgonBox>

              <ArgonBox
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e9ecef",
                }}
              >
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  User
                </ArgonTypography>
                <ArgonTypography variant="button" color="text" fontWeight="bold" display="block">
                  {selectedUser?.fullName || selectedUser?.name || "N/A"}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary">
                  {selectedUser?.email || "N/A"}
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.25}>
                {detailRows.map(([label, value]) => (
                  <ArgonBox
                    key={label}
                    sx={{
                      p: 1.25,
                      borderRadius: "10px",
                      border: "1px solid #e9ecef",
                      backgroundColor: "#fff",
                    }}
                  >
                    <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                      {label}
                    </ArgonTypography>
                    <ArgonTypography
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                      sx={{ display: "block", wordBreak: "break-word" }}
                    >
                      {value}
                    </ArgonTypography>
                  </ArgonBox>
                ))}
              </ArgonBox>
            </ArgonBox>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2.5, py: 1.75, backgroundColor: "#f8fafc" }}>
          <ArgonButton
            color="info"
            variant="gradient"
            onClick={() => setSelectedTransaction(null)}
            sx={{ minWidth: 110, borderRadius: "10px" }}
          >
            Close
          </ArgonButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
